import * as React from 'react';

import { faSidebarFlip } from '@fortawesome/pro-regular-svg-icons';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import SidePanel from 'src/components/sidePanel/SidePanel';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import DetailsSection from 'src/features/reservations/components/DetailsSection';
import ReservationDetailsSidePanel from 'src/features/reservations/components/ReservationDetailsSidePanel';
import { useReservationPaymentOrdersList } from 'src/features/reservations/hooks/useReservationPaymentOrders';
import { useReservationVehicleStay } from 'src/features/reservations/hooks/useReservationVehicleStay';
import { RESERVATION_TYPE } from 'src/features/reservations/types/reservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import {
    PaymentStatusBadgeReservationDetails,
    ReservationStatusBadge,
    ReservationTypeBadge,
} from 'src/features/reservations/utils/utils';
import { useMediaQuery } from 'src/hooks/use-media-query';
import { useParking } from 'src/providers/ParkingProvider';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Button } from 'src/registry/ui/Button';
import { Dialog, DialogContent } from 'src/registry/ui/Dialog';
import { useTitleStore } from 'src/stores/useTitleStore';
import { cn } from 'src/utils';
import { formatTime } from 'src/utils/formatTime';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';
import { generalPriceMessages, generalMessages } from 'src/utils/messages';

const ReservationDetails = () => {
    const { formatMessage, formatNumber } = useIntl();
    const { reservationId } = useParams();
    const { parkingId } = useParking();
    const { setTitle, removeTitle } = useTitleStore();
    const { setForceSidePanel, openPanel } = useSidePanel();

    const isSmallScreen = useMediaQuery('(max-width: 1224px)');
    const isMobile = useMediaQuery('(max-width: 639px)');

    const { data: vehiclesReservationData, refetch } = useReservationVehicleStay({
        parkingId: parkingId,
        reservationId: reservationId || '',
        config: { enabled: !!reservationId },
    });

    const { data: reservationPaymentOrders, refetch: refetchPaymentOrders } = useReservationPaymentOrdersList({
        parkingId: parkingId,
        reservationId: reservationId || '',
        config: { enabled: !!reservationId },
    });

    React.useEffect(() => {
        setTitle(formatMessage(reservationMessages.reservation));
        setForceSidePanel(true);
        return () => {
            removeTitle();
            setForceSidePanel(false);
        };
    }, []);

    const getDisplayDate = (dateStr?: string) => {
        if (!dateStr) return '-';
        const date = dayjs(dateStr);
        return date.format('DD/MM/YYYY HH:mm');
    };

    const additionalPropsForMobile = isMobile && {
        button: {
            title: formatMessage(reservationMessages.actions),
            onClick: () => openPanel('reservation-mobile-side-panel'),
            icon: faSidebarFlip,
        },
    };

    const getReservationDuration = () => {
        if (vehiclesReservationData) {
            const from = new Date(vehiclesReservationData.from_time);
            const to = new Date(vehiclesReservationData.to_time);

            const diffMs = to.getTime() - from.getTime(); // difference in ms (UTC)
            const totalMinutes = Math.floor(diffMs / 1000 / 60);
            const days = Math.floor(totalMinutes / (60 * 24));
            const hours = Math.floor((totalMinutes % (60 * 24)) / 60);

            if (vehiclesReservationData.vehicle_stay_reservation_type.reservation_type === RESERVATION_TYPE.HOURLY) {
                if (days > 0) {
                    return `${formatMessage(reservationMessages.days, { value: days })} ${formatMessage(reservationMessages.hours, { value: hours })}`;
                }
                return formatMessage(reservationMessages.hours, { value: hours });
            }

            return formatMessage(reservationMessages.days, { value: days });
        }

        return '-';
    };

    return (
        <div>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to="/vehicles/reservations">
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(reservationMessages.backReservations)}
                </Link>
            </Button>
            <div className="grid">
                <div className="flex flex-col">
                    <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                        <DetailsSection
                            title={formatMessage(reservationMessages.reservationDetails)}
                            data={[
                                {
                                    title: formatMessage(reservationMessages.reservationNumber),
                                    value: vehiclesReservationData?.number ? `#${vehiclesReservationData.number}` : '-',
                                },
                                {
                                    title: formatMessage(reservationMessages.reservationType),
                                    value: <ReservationTypeBadge vehiclesReservationData={vehiclesReservationData} />,
                                },
                                {
                                    title: formatMessage(reservationMessages.status),
                                    value: <ReservationStatusBadge vehiclesReservationData={vehiclesReservationData} />,
                                },
                                { title: formatMessage(reservationMessages.duration), value: getReservationDuration() },
                                {
                                    title: formatMessage(reservationMessages.reservationCreatedAt),
                                    value: getDisplayDate(vehiclesReservationData?.created_at),
                                },
                            ]}
                            {...additionalPropsForMobile}
                            className="mt-6"
                        />
                        <DetailsSection
                            isSubSection={isSmallScreen}
                            data={[
                                {
                                    title: formatMessage(reservationMessages.beginning),
                                    value: getDisplayDate(vehiclesReservationData?.from_time),
                                },
                                {
                                    title: formatMessage(reservationMessages.ending),
                                    value: getDisplayDate(vehiclesReservationData?.to_time),
                                },
                                {
                                    title: formatMessage(reservationMessages.allowedEntryAt),
                                    value: getDisplayDate(vehiclesReservationData?.from_time_with_tolerance),
                                },
                                {
                                    title: formatMessage(reservationMessages.allowedExitAt),
                                    value: getDisplayDate(vehiclesReservationData?.to_time_with_tolerance),
                                },
                                {
                                    title: formatMessage(reservationMessages.doesMembershipExists),
                                    value: vehiclesReservationData?.does_membership_exists
                                        ? formatMessage(generalMessages.yes)
                                        : formatMessage(generalMessages.no),
                                },
                            ]}
                            {...additionalPropsForMobile}
                            className="mt-6"
                        />
                    </div>
                    {vehiclesReservationData?.additional_services?.map((service, index) => {
                        return (
                            <div
                                className={cn('grid grid-cols-1 gap-2 xl:grid-cols-2', index !== 0 && 'mt-2')}
                                key={service.id}
                            >
                                <DetailsSection
                                    isSubSection={index !== 0}
                                    title={
                                        index === 0
                                            ? formatMessage(parkingManagementMessages.additionalServices)
                                            : undefined
                                    }
                                    data={[
                                        {
                                            title: formatMessage(generalMessages.name),
                                            value: service?.name,
                                        },
                                    ]}
                                    className="mt-8"
                                />
                                <DetailsSection
                                    isSubSection={isSmallScreen || index !== 0}
                                    data={[
                                        {
                                            title: formatMessage(reservationMessages.price),
                                            value: formatNumber(service.price_with_tax / 100, {
                                                style: 'currency',
                                                currency: service.currency,
                                            }),
                                        },
                                    ]}
                                    className="mt-8"
                                />
                            </div>
                        );
                    })}

                    <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                        <DetailsSection
                            title={formatMessage(reservationMessages.clientInformation)}
                            data={[
                                {
                                    title: formatMessage(reservationMessages.licensePlate),
                                    value: vehiclesReservationData?.license_plate,
                                },
                                {
                                    title: formatMessage(reservationMessages.name),
                                    value: vehiclesReservationData?.client_name,
                                },
                            ]}
                            className="mt-8"
                        />
                        <DetailsSection
                            isSubSection={isSmallScreen}
                            data={[
                                {
                                    title: formatMessage(reservationMessages.email),
                                    value: vehiclesReservationData?.client_email,
                                },
                                {
                                    title: formatMessage(reservationMessages.phoneNumber),
                                    value: vehiclesReservationData?.client_phone,
                                },
                            ]}
                            className="mt-8"
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                        {reservationPaymentOrders &&
                            reservationPaymentOrders.map((paymentOrder, index) => {
                                const paymentAttempts = reservationPaymentOrders.length - index;
                                const reservationNumber = paymentOrder.number
                                    ? `#${paymentAttempts} - ${paymentOrder.number}`
                                    : `#${paymentAttempts}`;
                                return (
                                    <React.Fragment key={paymentOrder.id}>
                                        <DetailsSection
                                            title={`${formatMessage(reservationMessages.payment)} ${reservationNumber}`}
                                            data={[
                                                {
                                                    title: formatMessage(reservationMessages.paymentStatus),
                                                    value: (
                                                        <PaymentStatusBadgeReservationDetails
                                                            paymentStatus={paymentOrder.status}
                                                        />
                                                    ),
                                                },
                                                {
                                                    title: formatMessage(reservationMessages.paymentOperator),
                                                    value: paymentOrder.provider?.toUpperCase(),
                                                },
                                                {
                                                    title: formatMessage(reservationMessages.transactionId),
                                                    value: paymentOrder.payment_transaction_number,
                                                },
                                                {
                                                    title: formatMessage(
                                                        reservationMessages.reservationPaymentOrderCreatedAt,
                                                    ),
                                                    value: getDisplayDate(paymentOrder.created_at),
                                                },
                                                {
                                                    title: formatMessage(reservationMessages.completedOn),
                                                    value: getDisplayDate(paymentOrder.payment_completed_at),
                                                },
                                            ]}
                                            className="mt-8"
                                        />
                                        <DetailsSection
                                            isSubSection={isSmallScreen}
                                            data={[
                                                {
                                                    title: formatMessage(reservationMessages.ip),
                                                    value: paymentOrder.client_ip,
                                                },
                                                {
                                                    title: formatMessage(generalPriceMessages.currency),
                                                    value: vehiclesReservationData?.vehicle_stay_reservation_type
                                                        .currency,
                                                },
                                                {
                                                    title: formatMessage(generalPriceMessages.price),
                                                    value: formatNumber(paymentOrder.due_amount / 100, {
                                                        style: 'currency',
                                                        currency:
                                                            vehiclesReservationData?.vehicle_stay_reservation_type
                                                                .currency,
                                                    }),
                                                },
                                                {
                                                    title: `${formatMessage(generalPriceMessages.vat)} %`,
                                                    value: `${(Number(paymentOrder.tax_percent) * 100).toFixed(2)}%`,
                                                },
                                                {
                                                    title: formatMessage(generalPriceMessages.priceWithVat),
                                                    value: formatNumber(paymentOrder.due_amount_with_tax / 100, {
                                                        style: 'currency',
                                                        currency:
                                                            vehiclesReservationData?.vehicle_stay_reservation_type
                                                                .currency,
                                                    }),
                                                },
                                            ]}
                                            className="mt-8"
                                        />
                                    </React.Fragment>
                                );
                            })}
                    </div>
                </div>
                {!isMobile && (
                    <Dialog modal={false} open={true}>
                        <DialogContent variant="sheet" showCloseButton={false}>
                            <ReservationDetailsSidePanel
                                reservation={vehiclesReservationData}
                                reservationPaymentOrder={reservationPaymentOrders && reservationPaymentOrders[0]}
                                refetch={refetch}
                                refetchPaymentOrders={refetchPaymentOrders}
                            />
                        </DialogContent>
                    </Dialog>
                )}
                {isMobile && (
                    <SidePanel name="reservation-mobile-side-panel">
                        <ReservationDetailsSidePanel
                            reservation={vehiclesReservationData}
                            isMobile
                            reservationPaymentOrder={reservationPaymentOrders && reservationPaymentOrders[0]}
                            refetch={refetch}
                            refetchPaymentOrders={refetchPaymentOrders}
                        />
                    </SidePanel>
                )}
            </div>
        </div>
    );
};

export default ReservationDetails;
